import * as moment from "moment";
/** Maximum number of records submitted in a single import request. */
export const MAX_IMPORT_RECORDS = 2000;

/**
 * Delimiter used inside a single CSV cell for all multi-valued fields.
 * A pipe (`|`) separates:
 * - simple multi-values such as alias names, phone numbers and email addresses.
 * - multiple addresses across the three parallel address columns
 *   (address / city / country), aligned by position.
 * A pipe is used (rather than a comma) because a physical/postal address itself
 * frequently contains commas.
 */
export const MULTI_VALUE_DELIMITER = "|";
export const ADDRESS_DELIMITER = "|";

export type PersonFieldKey =
  | "Title"
  | "FirstName"
  | "MiddleName"
  | "Surname"
  | "Alias"
  | "Gender"
  | "DateOfBirth"
  | "CurrentNationality"
  | "PhoneNumber"
  | "EmailAddress"
  | "WebsiteOrLink"
  | "PhysicalAddress"
  | "PhysicalAddressCity"
  | "PhysicalAddressCountry"
  | "PostalAddress"
  | "PostalAddressCity"
  | "PostalAddressCountry";

export type FieldMapping = Partial<Record<PersonFieldKey, string>>;

export type ImportStatus = "idle" | "running" | "done";

export type ImportResults = {
  total: number;
  successful: number;
  failed: number;
  downloadUrl: string | null;
};

export const PERSON_FIELDS: Array<{
  key: PersonFieldKey;
  label: string;
  required?: boolean;
}> = [
  { key: "Title", label: "Title" },
  { key: "FirstName", label: "First Name", required: true },
  { key: "MiddleName", label: "Middle Name" },
  { key: "Surname", label: "Surname", required: true },
  { key: "Alias", label: "Alias Names" },
  { key: "Gender", label: "Gender" },
  { key: "DateOfBirth", label: "Date of Birth" },
  { key: "CurrentNationality", label: "Current Nationality" },
  { key: "PhoneNumber", label: "Phone Number" },
  { key: "EmailAddress", label: "Email Address" },
  { key: "WebsiteOrLink", label: "Website or Link" },
  { key: "PhysicalAddress", label: "Physical Address" },
  { key: "PhysicalAddressCity", label: "Physical Address City" },
  { key: "PhysicalAddressCountry", label: "Physical Address Country" },
  { key: "PostalAddress", label: "Postal Address" },
  { key: "PostalAddressCity", label: "Postal Address City" },
  { key: "PostalAddressCountry", label: "Postal Address Country" },
];

function normalizeStr(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function similarityScore(a: string, b: string): number {
  if (a === b) return 2;
  if (a.includes(b) || b.includes(a)) {
    return Math.min(a.length, b.length) / Math.max(a.length, b.length);
  }
  return 0;
}

export function autoMapFields(csvHeaders: string[]): FieldMapping {
  const mapping: FieldMapping = {};
  const claimed = new Set<string>();

  for (const field of PERSON_FIELDS) {
    // Match against both the field key and its human-readable label so that
    // headers like "Alias Names" or "Physical Address City" map cleanly.
    const normKey = normalizeStr(field.key);
    const normLabel = normalizeStr(field.label);
    let bestHeader = "";
    let bestScore = 0;

    for (const header of csvHeaders) {
      if (claimed.has(header)) continue;
      const normHeader = normalizeStr(header);
      const score = Math.max(
        similarityScore(normKey, normHeader),
        similarityScore(normLabel, normHeader)
      );
      if (score > bestScore) {
        bestScore = score;
        bestHeader = header;
      }
    }

    if (bestScore > 0) {
      mapping[field.key] = bestHeader;
      claimed.add(bestHeader);
    }
  }

  return mapping;
}

/** Parses a DD/MM/YYYY date string into YYYY-MM-DD (ISO), or returns null. */
function parseDateOfBirth(raw: string | null): string | null {
  if (!raw) return null;
  const match = raw.match(/^(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})$/);
  if (!match) return raw;
  const [, d, m, y] = match;
  return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
}

export function mapRowToRecord(
  row: Record<string, string>,
  mappings: FieldMapping,
) {
  const get = (key: PersonFieldKey): string | null => {
    const col = mappings[key];
    if (!col) return null;
    return row[col]?.trim() || null;
  };

  return {
    title: get("Title"),
    firstName: get("FirstName") ?? "",
    middleName: get("MiddleName"),
    surname: get("Surname") ?? "",
    alias: get("Alias"),
    gender: get("Gender"),
    dateOfBirth:
      (parseDateOfBirth(get("DateOfBirth")) &&
        moment(get("DateOfBirth"), "DD/MM/YYYY")
          .toDate()
          .toLocaleDateString("en-CA")) ||
      null,
    currentNationality: get("CurrentNationality"),
    phoneNumber: get("PhoneNumber"),
    emailAddress: get("EmailAddress"),
    websiteOrLink: get("WebsiteOrLink"),
    physicalAddress: get("PhysicalAddress"),
    physicalAddressCity: get("PhysicalAddressCity"),
    physicalAddressCountry: get("PhysicalAddressCountry"),
    postalAddress: get("PostalAddress"),
    postalAddressCity: get("PostalAddressCity"),
    postalAddressCountry: get("PostalAddressCountry"),
  };
}
